import React, { useEffect, useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import Select from 'react-select'
import { connect } from 'react-redux'
import { addMinutes, endOfDay, startOfDay, isToday, subMinutes } from 'date-fns'
import { toZonedTime } from 'date-fns-tz'
import moment from 'moment-timezone'
import { CurrencyDollarIcon } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'

import {
  getBuildingOptionsPmOrFd,
  getCheckboxHandler,
  getFrequencyOptions,
  getIdFromObjectOrValue,
  getTimezone,
  getOption,
  handleTimeChange,
  transformFormStateToRequestData,
} from '../helpers'
import {
  fetchInitialData,
  getBuildingOptions,
  getEventTypeOptions,
  getTrainerOptions,
} from '../Steps/data/adminManager.data'
import API from '../../../../api/api'
import { selectUserData } from '../../../../reducers/userDataReducer'
import { selectActiveBuilding } from '../../../../reducers/buildingSelectorReducer'
import { getAssetLink } from '../../../../helpers/get-asset-link'
import { useSnackbar } from '../../../../helpers/hooks/useSnackbar'
import BaseEvent from '../BaseEvent/BaseEvent'
import SetupAutomatedReminders from '../SetupAutomatedReminders/SetupAutomatedReminders'
import TextField from '../../../FormLegacy/FormFieldsV2/TextField'
import TextAreaField from '../../../FormLegacy/FormFieldsV2/TextAreaField'
import CheckboxField from '../../../FormLegacy/FormFieldsV2/CheckboxField'
import DateField from '../../../FormLegacy/FormFieldsV2/DateField'
import NumericFieldWithIncrement from '../../../FormLegacy/FormFieldsV2/NumericFieldWithIncrement'
import ToggleField from '../../../FormLegacy/FormFieldsV2/ToggleField'
import Icon from '../../../Icons'
import Loader from '../../../Loader/Loader'
import ConfirmPopup from '../../../Popups/ConfirmPopup'
import { useFetchSocialLinks } from '../../hooks'
import EditEvent from '../EditEvent/EditEvent'
import EditEventsSchedule from '../EditEventsSchedule/EditEventsSchedule'
import ImagesDatabase from '../ImagesDatabase/ImagesDatabase'
import MediaUploadInput from '../MediaUploadInput/MediaUploadInput'
import MediaUploadInputEditMode from '../MediaUploadInputEditMode/MediaUploadInputEditMode'
import MobilePreview from '../../../EventManagement/common/components/mobilePreview/MobilePreview'
import PDFUpload from '../PDFUpload/PDFUpload'
import PositiveReviewFollowup from '../PositiveReviewFollowup/PositiveReviewFollowup'

import { useGetBuildingFormState } from './hooks'

const BuildingEvent = ({
  editMode,
  eventData,
  goBack,
  handleClose,
  repeatable,
  selectedBuildingId,
  userData,
}) => {
  const { t } = useTranslation(['create-event', 'common'])
  const [fetchState, setFetchState] = useState({})
  const [eventsToUpdate, setEventsToUpdate] = useState('false')
  const [isPopupOpen, togglePopup] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { displaySnackbar } = useSnackbar()

  const isPropertyManager = userData.user.role === 'PropertyManager'
  const isFrontDesk = userData.user.role === 'ShiftManager'
  const isPropertyManagerOrFrontDesk = ['PropertyManager', 'ShiftManager'].includes(
    userData.user.role,
  )
  const isManagerOrAdmin = ['Manager', 'Administrator'].includes(userData.user.role)

  const buildingOptions = isPropertyManagerOrFrontDesk
    ? getBuildingOptionsPmOrFd(isPropertyManager, isFrontDesk, userData)
    : fetchState.buildings
      ? getBuildingOptions(fetchState.buildings)
      : []

  const [formState, setFormState] = useGetBuildingFormState(
    eventData,
    editMode,
    selectedBuildingId,
    buildingOptions,
    t,
  )

  const socialLinks = useFetchSocialLinks(formState.building)

  const trainerOptions = fetchState.trainers ? getTrainerOptions(fetchState.trainers) : []
  const eventTypeOptions = fetchState.trainingTypes
    ? getEventTypeOptions(fetchState.trainingTypes)
    : []

  const detailButtonEnabled = isPropertyManagerOrFrontDesk
    ? !!formState.building && !!formState.eventName && !!formState.description
    : !!formState.building &&
      !!formState.eventType &&
      !!formState.trainer &&
      !!formState.eventName &&
      !!formState.description

  const dateAndTimeButtonEnabled =
    !!formState.location &&
    !!formState.date &&
    !!formState.timeStart &&
    !!formState.timeEnd &&
    !!formState.eventCapacity > 0

  const eventCapacityError = formState.eventCapacity < 1
  const guestsAmountError =
    formState.guestsAmount < 0 || formState.guestsAmount > formState.eventCapacity
  const eventPriceError = formState.eventPrice < 0
  const submitButtonEnabled =
    !isSubmitting && !eventCapacityError && !guestsAmountError && !eventPriceError

  const timezone = useMemo(
    () => getTimezone(formState, buildingOptions),
    [buildingOptions, formState.building],
  )

  const { minStartTime, maxStartTime, minEndTime, maxEndTime } = useMemo(() => {
    const selectedDate = new Date(formState.date)

    const minTime = isToday(selectedDate)
      ? toZonedTime(new Date(), timezone)
      : startOfDay(toZonedTime(selectedDate, timezone))
    const maxTime = endOfDay(minTime)

    return {
      minStartTime: minTime,
      maxStartTime: subMinutes(maxTime, 15),
      minEndTime: addMinutes(minTime, 15),
      maxEndTime: maxTime,
    }
  }, [formState.date, timezone])

  useEffect(() => {
    if (isManagerOrAdmin) {
      ;(async () => {
        const data = await fetchInitialData()
        setFetchState((prev) => ({ ...prev, ...data }))
      })()
    }
  }, [])

  useEffect(() => {
    if (editMode && eventData && timezone) {
      setFormState((prev) => ({
        ...prev,
        date: moment(eventData.date).tz(timezone).toDate(),
      }))
    }
  }, [editMode, timezone, eventData])

  useEffect(() => {
    if (editMode && eventData && timezone) {
      setFormState((prev) => ({
        ...prev,
        date: moment(eventData.date).tz(timezone).toDate(),
      }))
    }
  }, [editMode, timezone, eventData])

  const onSubmit = async () => {
    setIsSubmitting(true)
    const formData = await transformFormStateToRequestData(
      {
        ...formState,
        buildingData: buildingOptions.find(
          (b) => b.id === getIdFromObjectOrValue(formState.building),
        ),
      },
      'Building Event',
      eventTypeOptions,
      isPropertyManagerOrFrontDesk,
      socialLinks,
    )
    try {
      await API.createNewEvent(formData)
      displaySnackbar(
        t('create-event:buildingEvent.createdSuccessfully', { eventName: formState.eventName }),
      )
      handleClose()
    } catch {
      displaySnackbar(
        t('create-event:buildingEvent.errorCreating', { eventName: formState.eventName }),
        true,
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const onSubmitEdit = () => {
    if (repeatable) {
      togglePopup(true)
    } else {
      setEventsToUpdate(false)
      editModeConfirmCallback()
    }
  }

  const editModeConfirmCallback = async () => {
    setIsSubmitting(true)
    const newData = await transformFormStateToRequestData(
      {
        ...formState,
        buildingData: buildingOptions.find(
          (b) => b.id === getIdFromObjectOrValue(formState.building),
        ),
      },
      'Building Event',
      eventTypeOptions,
      isPropertyManagerOrFrontDesk,
      socialLinks,
    )
    togglePopup(false)
    API.updateEvent(eventData.id, newData, eventsToUpdate)
      .then(() => displaySnackbar(t('create-event:shared.changesSavedSuccessfully')))
      .catch((error) => {
        try {
          const errorMessage = JSON.parse(error).message
          if (
            errorMessage &&
            errorMessage.includes('Updating the date for a repeating event is not possible')
          ) {
            displaySnackbar(errorMessage, true)
          } else {
            displaySnackbar(t('create-event:shared.errorSavingChanges'), true)
          }
        } catch {
          displaySnackbar(t('create-event:shared.errorSavingChanges'), true)
        }
      })
      .finally(() => {
        setIsSubmitting(false)
        handleClose()
      })
  }

  const editModeCancelCallback = () => {
    togglePopup(false)
  }

  const handleFieldChange = ({ target: { name, value } }) => {
    if (name === 'date') {
      const newFrequencyOptions = getFrequencyOptions(value)

      setFormState((prev) => {
        if (prev.schedulerType && prev.schedulerType.value !== 'none') {
          const currentType = prev.schedulerType.value
          const updatedOption = newFrequencyOptions.find((opt) => opt.value === currentType)

          return {
            ...prev,
            [name]: value,
            schedulerType: updatedOption || prev.schedulerType,
          }
        }

        // Otherwise just update the date
        return { ...prev, [name]: value }
      })
    } else {
      setFormState((prev) => ({ ...prev, [name]: value }))
    }
  }

  const handleCheckboxChange = getCheckboxHandler(handleFieldChange)

  const isLoading = !buildingOptions.length && !eventTypeOptions.length && !trainerOptions.length
  const showHouseholdMembersToggle = editMode
    ? formState.isHoa
    : formState.building && formState.building.isHoa

  const config = {
    details: {
      header: t('create-event:baseEvent.steps.eventDetails'),
      canGoNext: detailButtonEnabled,
      inputs: {
        first: {
          render: () => (
            <Select
              className="select-container"
              classNamePrefix="select-primary"
              options={buildingOptions}
              name="building"
              placeholder={t('create-event:shared.selectBuildingPlaceholder')}
              value={getOption(formState.building, buildingOptions)}
              onChange={(e) => setFormState((prev) => ({ ...prev, building: e }))}
              selectStyle={{ width: '100%' }}
            />
          ),
          label: t('create-event:shared.selectBuildingLabel'),
        },
        second: !isPropertyManagerOrFrontDesk
          ? {
              render: () => (
                <Select
                  className="select-container"
                  classNamePrefix="select-primary"
                  name="eventType"
                  options={eventTypeOptions}
                  placeholder={t('create-event:buildingEvent.selectEventTypePlaceholder')}
                  onChange={(e) => setFormState((prev) => ({ ...prev, eventType: e }))}
                  value={getOption(formState.eventType, eventTypeOptions)}
                  selectStyle={{ width: '100%' }}
                />
              ),
              label: t('create-event:buildingEvent.selectEventTypeLabel'),
            }
          : null,
        third: !isPropertyManagerOrFrontDesk
          ? {
              render: () => (
                <Select
                  name="trainer"
                  className="select-container"
                  classNamePrefix="select-primary"
                  options={trainerOptions}
                  placeholder={t('create-event:buildingEvent.selectEventFacilitatorPlaceholder')}
                  onChange={(e) => setFormState((prev) => ({ ...prev, trainer: e }))}
                  value={getOption(formState.trainer, trainerOptions)}
                  selectStyle={{ width: '100%' }}
                />
              ),
              label: t('create-event:buildingEvent.selectEventFacilitatorLabel'),
            }
          : null,
        fourth: null,
        fifth: {
          render: () => (
            <TextField
              name="eventName"
              onChange={handleFieldChange}
              value={formState.eventName}
              placeholder={t('create-event:buildingEvent.enterEventNamePlaceholder')}
              maxLength={100}
            />
          ),
          label: t('create-event:buildingEvent.eventNameLabel'),
        },
        sixth: {
          render: () => (
            <TextAreaField
              name="description"
              onChange={handleFieldChange}
              value={formState.description}
              placeholder={t('create-event:buildingEvent.eventDescriptionPlaceholder')}
              maxLength={1000}
            />
          ),
          label: t('create-event:buildingEvent.eventDescriptionLabel'),
        },
      },
    },
    mediaUpload: {
      header: t('create-event:progressTimeline.mediaUpload'),
      canGoNext: !!formState.image,
      editMode: editMode,
      inputs: {
        first: {
          label: formState.imagesDatabaseOpen ? '' : t('create-event:shared.uploadEventPhotoLabel'),
          render: () =>
            editMode ? (
              <MediaUploadInputEditMode
                file={formState.image}
                handleFieldChange={handleFieldChange}
                buttonLabel={t('create-event:shared.replacePhotoButtonLabel')}
                name="image"
                accept="image/*"
                maxFileNameWidth="150px"
              />
            ) : formState.imagesDatabaseOpen ? (
              <ImagesDatabase formState={formState} handleFieldChange={handleFieldChange} />
            ) : (
              <MediaUploadInput
                eventName={formState.eventName}
                formState={formState}
                handleFieldChange={handleFieldChange}
              />
            ),
        },
        image:
          editMode && formState.image
            ? {
                image:
                  typeof formState.image === 'string'
                    ? getAssetLink({
                        path: 'chitown-images/training',
                        file: formState.image,
                        fallback: { file: 'training_image.png' },
                      })
                    : URL.createObjectURL(formState.image),
              }
            : null,
        second: {
          label: t('create-event:shared.uploadPdfOptionalLabel'),
          labelIcon: 'pdf',
          render: () =>
            editMode ? (
              <MediaUploadInputEditMode
                file={formState.pdf}
                handleFieldChange={handleFieldChange}
                buttonLabel={
                  formState.pdf
                    ? t('create-event:shared.replacePdfButtonLabel')
                    : t('create-event:shared.uploadPdfButtonLabel')
                }
                accept="application/pdf"
                name="pdf"
                maxFileNameWidth="250px"
              />
            ) : (
              <PDFUpload handleFieldChange={handleFieldChange} file={formState.pdf} />
            ),
        },
      },
    },
    dateAndTime: {
      header: t('create-event:baseEvent.steps.dateTime'),
      canGoNext: dateAndTimeButtonEnabled,
      inputs: {
        first: {
          render: () => (
            <TextField
              leftIcon={<Icon icon="location2" />}
              name="location"
              placeholder={t('create-event:buildingEvent.enterEventLocationPlaceholder')}
              onChange={handleFieldChange}
              value={formState.location}
            />
          ),
          label: t('create-event:shared.locationRequiredLabel'),
        },
        second: {
          render: () => (
            <DateField
              popperPlacement="bottom-start"
              name="date"
              minDate={moment().toDate()}
              value={formState.date}
              onChange={handleFieldChange}
            />
          ),
          label: t('create-event:buildingEvent.eventDateAndTime'),
        },
        third: {
          render: () => (
            <DateField
              popperPlacement="bottom-start"
              showTimeSelect
              showTimeSelectOnly
              timeIntervals={15}
              dateFormat="h:mm aa"
              minTime={minStartTime}
              maxTime={maxStartTime}
              name="timeStart"
              value={formState.timeStart}
              onChange={(e) =>
                handleTimeChange(e, handleFieldChange, formState.timeStart, formState.timeEnd)
              }
            />
          ),
          label: t('create-event:shared.startsLabel'),
        },
        fourth: {
          render: () => (
            <DateField
              popperPlacement="bottom-start"
              showTimeSelect
              showTimeSelectOnly
              timeIntervals={15}
              dateFormat="h:mm aa"
              minTime={minEndTime}
              maxTime={maxEndTime}
              name="timeEnd"
              value={formState.timeEnd}
              onChange={(e) =>
                handleTimeChange(e, handleFieldChange, formState.timeStart, formState.timeEnd)
              }
            />
          ),
          label: t('create-event:shared.endsLabel'),
        },
        fifth: editMode
          ? null
          : {
              render: () => (
                <Select
                  className="select-container"
                  classNamePrefix="select-primary"
                  options={getFrequencyOptions(formState.date, t)}
                  name="schedulerType"
                  placeholder={t('create-event:shared.selectFrequencyPlaceholder')}
                  value={formState.schedulerType}
                  onChange={(e) => setFormState((prev) => ({ ...prev, schedulerType: e }))}
                  selectStyle={{ width: '100%' }}
                />
              ),
              label: t('create-event:shared.frequencyLabel'),
            },
        sixth: null,
        seventh: null,
      },
    },
    summary: {
      header: t('create-event:baseEvent.steps.attendees'),
      canGoNext: submitButtonEnabled,
      onSubmit: editMode ? onSubmitEdit : onSubmit,
      inputs: {
        first: {
          render: () => (
            <NumericFieldWithIncrement
              onlyPositive
              isError={eventCapacityError}
              name="eventCapacity"
              onChange={handleFieldChange}
              onIncrement={handleFieldChange}
              onDecrement={handleFieldChange}
              value={formState.eventCapacity}
            />
          ),
          label: t('create-event:buildingEvent.enterEventCapacity'),
        },
        second: {
          render: () => (
            <NumericFieldWithIncrement
              onlyPositive
              isError={guestsAmountError}
              name="guestsAmount"
              onChange={handleFieldChange}
              value={formState.guestsAmount}
              onIncrement={handleFieldChange}
              onDecrement={handleFieldChange}
              errorLabel={
                guestsAmountError && t('create-event:buildingEvent.guestsAllowedPerResidentError')
              }
            />
          ),
          label: t('create-event:buildingEvent.guestsAllowedPerResident'),
        },
        third: {
          render: () => (
            <CheckboxField
              name="collectGuestInformation"
              onChange={handleCheckboxChange}
              value={formState.collectGuestInformation}
              disabled={formState.guestsAmount < 0}
            />
          ),
          label: t('create-event:buildingEvent.collectGuestInformation'),
          tooltips: t('create-event:buildingEvent.collectGuestInformationTooltip', {
            returnObjects: true,
          }),
        },
        fourth: {
          render: () => (
            <NumericFieldWithIncrement
              onlyPositive
              isError={eventPriceError}
              name="eventPrice"
              onChange={handleFieldChange}
              value={formState.eventPrice}
              onIncrement={handleFieldChange}
              onDecrement={handleFieldChange}
            />
          ),
          label: t('create-event:buildingEvent.eventPrice'),
          labelIcon: <CurrencyDollarIcon size={20} />,
        },
        fifth: {
          render: () => (
            <CheckboxField
              name="askForPostEventReview"
              onChange={handleCheckboxChange}
              value={formState.askForPostEventReview}
            />
          ),
          renderExtra: () =>
            formState.askForPostEventReview && (
              <PositiveReviewFollowup
                options={socialLinks || []}
                handleCheckboxChange={handleCheckboxChange}
                handleFieldChange={handleFieldChange}
                formState={formState}
                tooltips={t('create-event:shared.positiveReviewFollowupTooltip', {
                  returnObjects: true,
                })}
              />
            ),
          label: t('create-event:shared.askForPostEventReviewLabel'),
          tooltips: t('create-event:shared.askForPostEventReviewTooltip', { returnObjects: true }),
        },
        sixth: {
          render: () => (
            <CheckboxField
              name="setupAutomatedReminders"
              onChange={handleCheckboxChange}
              value={formState.setupAutomatedReminders}
            />
          ),
          renderExtra: () =>
            formState.setupAutomatedReminders && (
              <SetupAutomatedReminders
                formState={formState}
                handleCheckboxChange={handleCheckboxChange}
                handleFieldChange={handleFieldChange}
              />
            ),
          label: t('create-event:shared.setupAutomatedRemindersLabel'),
          tooltips: t('create-event:shared.setupAutomatedRemindersTooltip', {
            returnObjects: true,
          }),
        },
        seventh: {
          render: () => (
            <CheckboxField
              name="collectRSVP"
              onChange={handleCheckboxChange}
              value={formState.collectRSVP}
            />
          ),
          renderExtra: () =>
            formState.collectRSVP && (
              <React.Fragment>
                <TextField
                  name="signUpLink"
                  onChange={handleFieldChange}
                  value={formState.signUpLink}
                  placeholder={t('create-event:buildingEvent.enterSignupLinkOrEmailPlaceholder')}
                  style={{ marginLeft: '40px' }}
                />
                <div className="description">
                  {t('create-event:buildingEvent.signupLinkDescription')}
                </div>
              </React.Fragment>
            ),
          label: t('create-event:buildingEvent.collectRsvpsByExternalUrlOrEmail'),
        },
        eighth: showHouseholdMembersToggle
          ? {
              render: () => (
                <ToggleField
                  className="toggle-field"
                  name="householdMembersAllowed"
                  value={formState.householdMembersAllowed}
                  onChange={handleCheckboxChange}
                />
              ),
              label: t('create-event:buildingEvent.allowAddingHouseholdMembers'),
              tooltips: t('create-event:buildingEvent.allowAddingHouseholdMembersTooltip', {
                returnObjects: true,
              }),
            }
          : null,
      },
    },
    footer: {
      cta: editMode
        ? t('create-event:shared.saveChangesCta')
        : t('create-event:buildingEvent.createABuildingEventCta'),
    },
  }

  return (
    <div className="events-modal">
      <div className="events-modal-card">
        {isLoading ? (
          <Loader style={{ width: '100px', height: '100px', left: '44%', top: '40%' }} />
        ) : editMode ? (
          <EditEvent config={config} eventType="buildingEvent" handleClose={handleClose} />
        ) : (
          <BaseEvent
            config={config}
            goBack={goBack}
            eventType="buildingEvent"
            handleClose={handleClose}
          />
        )}
        <ConfirmPopup
          isOpen={isPopupOpen}
          cancelCallback={editModeCancelCallback}
          confirmCallback={editModeConfirmCallback}
          cancelText={t('common:actions.cancel')}
          confirmText={t('common:actions.confirm')}
          title={t('create-event:shared.cautionTitle')}
          body={
            <EditEventsSchedule
              onChange={(e) => setEventsToUpdate(e.target.value)}
              value={eventsToUpdate}
              formName="buildingEvent"
            />
          }
        />
      </div>
      <MobilePreview formState={formState} eventType="Event" trainerOptions={trainerOptions} />
    </div>
  )
}

BuildingEvent.propTypes = {
  editMode: PropTypes.bool,
  eventData: PropTypes.object,
  goBack: PropTypes.func.isRequired,
  handleClose: PropTypes.func,
  repeatable: PropTypes.bool,
  selectedBuildingId: PropTypes.number,
  userData: PropTypes.object,
}

export default connect((state) => {
  const selectedBuilding = selectActiveBuilding(state)
  return {
    userData: selectUserData(state),
    selectedBuildingId: selectedBuilding ? selectedBuilding.id : null,
  }
})(BuildingEvent)
